import { quoteFirstCleanPrice } from '@shared/first-clean-price.ts';
import type { QuoteChangesInput } from '@shared/schemas/quotes.ts';
import type { Quote, QuoteFrequencyTier, QuoteGroup, QuoteRoom } from '@shared/types/index.ts';
import type { SeriesFrequency } from '@shared/constants/index.ts';

/**
 * Reconciling the editor's working copy with a server response.
 *
 * Pure on purpose: this is where the concurrency rules live — which rows keep
 * which identity, and which side of a field wins when the user kept typing
 * while a request was in flight — and every one of them is worth testing
 * without a React tree around it.
 */

/**
 * New rooms and tasks get a temporary client id so React keys stay stable while
 * they are being edited. Only server-issued ULIDs are ever sent back.
 */
const ULID_PATTERN = /^[0-9A-HJKMNP-TV-Z]{26}$/;

export function isPersistedId(id: string | null | undefined): boolean {
  return typeof id === 'string' && ULID_PATTERN.test(id);
}

export function temporaryId(): string {
  return `tmp_${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Learns which server id each temporary id was given.
 *
 * Positional alignment only holds between what was *submitted* and what came
 * back, so the mapping is built from those two — never from whatever the local
 * array looks like now. If the user reordered rooms while the save was in
 * flight, matching the response against the current array by index would hand a
 * server id to the wrong row, and the resulting duplicate ids would make every
 * later save fail validation.
 */
export function buildIdMap(submitted: Quote, server: Quote): Map<string, string> {
  const map = new Map<string, string>();

  submitted.rooms.forEach((room, index) => {
    const serverRoom = server.rooms[index];
    if (!serverRoom) return;
    if (!isPersistedId(room.id)) map.set(room.id, serverRoom.id);

    room.services.forEach((service, serviceIndex) => {
      const serverService = serverRoom.services[serviceIndex];
      if (!serverService) return;
      if (!isPersistedId(service.id)) map.set(service.id, serverService.id);
    });
  });

  submitted.fees.forEach((fee, index) => {
    const serverFee = server.fees[index];
    if (serverFee && !isPersistedId(fee.id)) map.set(fee.id, serverFee.id);
  });

  (submitted.groups ?? []).forEach((group, index) => {
    const serverGroup = server.groups?.[index];
    if (serverGroup && !isPersistedId(group.id)) map.set(group.id, serverGroup.id);
  });

  return map;
}

/** Swaps the temporary ids in a local copy for the ids the server issued. */
export function resolveIds(local: Quote, idMap: Map<string, string>): Quote {
  return {
    ...local,
    groups: local.groups?.map((group) => ({
      ...group,
      id: isPersistedId(group.id) ? group.id : (idMap.get(group.id) ?? group.id),
    })),
    rooms: local.rooms.map((room) => ({
      ...room,
      id: isPersistedId(room.id) ? room.id : (idMap.get(room.id) ?? room.id),
      groupId: room.groupId && !isPersistedId(room.groupId) ? (idMap.get(room.groupId) ?? room.groupId) : room.groupId,
      services: room.services.map((service) => ({
        ...service,
        id: isPersistedId(service.id) ? service.id : (idMap.get(service.id) ?? service.id),
      })),
    })),
    fees: local.fees.map((fee) => ({
      ...fee,
      id: isPersistedId(fee.id) ? fee.id : (idMap.get(fee.id) ?? fee.id),
    })),
  };
}

/**
 * The `Quote` fields the editor itself writes.
 *
 * `graftLocalEdits` keeps exactly these from the local copy; everything else
 * — status, sharing, sent and viewed stamps, the PDF pointer, the derived
 * `onSiteMinutes` — comes from the server, which owns it.
 */
export const LOCAL_EDIT_FIELDS = [
  'areaPricing',
  'firstCleanPrice',
  'walkthrough',
  'dust',
  'customerId',
  'propertyId',
  'quoteDate',
  'expirationDate',
  // Top-level, unlike the rate and increment riding in `totals` — and the
  // derived onSiteMinutes deliberately stays the server's: the editor's own
  // surfaces divide live through the shared helper, so a stale quotient here
  // can never reach the screen.
  'crewSize',
  'internalNotes',
  'customerNotes',
  'termsSnapshot',
  'showTimeOnQuote',
  'showHourlyRateOnQuote',
  // The commercial shape: what the total buys, how many visits a month of it
  // covers, and the agreement wrapped around it.
  'billingBasis',
  'visitsPerWeek',
  'contract',
  'frequencyTiersEnabled',
  'preferredFrequency',
  // Kept in halves: the discount inputs are the editor's, the derived cents on
  // each tier are the Worker's (see the graft).
  'frequencyTiers',
  'totals',
  'groups',
  'rooms',
  'fees',
] as const satisfies readonly (keyof Quote)[];

export type LocalEditField = (typeof LOCAL_EDIT_FIELDS)[number];

// A margin override reason belongs to a deliberate approval request, not the
// quote's editable state. Autosaving must never replay an approval reason.
type QuoteAutosaveChanges = Omit<QuoteChangesInput, 'marginOverrideReason'>;

/**
 * Which local field each key `buildChanges` sends is read from.
 *
 * This is what keeps the two halves of the editor's concurrency contract in
 * step. A field the editor SENDS but the graft does not KEEP is an edit made
 * during an in-flight save that the response silently reverts — and the
 * reverted value is then what the next save writes.
 *
 * Two things make that impossible to reintroduce quietly: the `satisfies`
 * below fails to compile unless every key of the update payload names a
 * source, and a source can only be a field the graft preserves; and
 * `tests/unit/quote-merge.test.ts` checks the map against what `buildChanges`
 * actually returns and against what a graft actually keeps.
 */
export const CHANGE_FIELD_SOURCES = {
  areaPricing: 'areaPricing',
  firstCleanPrice: 'firstCleanPrice',
  dustAssessment: 'dust',
  dustContextSince: 'dust',
  walkthroughAssessment: 'walkthrough',
  customerId: 'customerId',
  propertyId: 'propertyId',
  quoteDate: 'quoteDate',
  expirationDate: 'expirationDate',
  hourlyRateCents: 'totals',
  billingIncrementMinutes: 'totals',
  crewSize: 'crewSize',
  discountType: 'totals',
  discountValue: 'totals',
  taxRateBasisPoints: 'totals',
  laborIsTaxable: 'totals',
  billingBasis: 'billingBasis',
  visitsPerWeek: 'visitsPerWeek',
  contractTermMonths: 'contract',
  contractAutoRenews: 'contract',
  contractEscalationBasisPoints: 'contract',
  contractCancellationNoticeDays: 'contract',
  internalNotes: 'internalNotes',
  customerNotes: 'customerNotes',
  terms: 'termsSnapshot',
  showTimeOnQuote: 'showTimeOnQuote',
  showHourlyRateOnQuote: 'showHourlyRateOnQuote',
  totalOverrideCents: 'totals',
  totalOverrideReason: 'totals',
  groups: 'groups',
  rooms: 'rooms',
  fees: 'fees',
  frequencyTiersEnabled: 'frequencyTiersEnabled',
  frequencyTiers: 'frequencyTiers',
  preferredFrequency: 'preferredFrequency',
} as const satisfies Record<keyof QuoteAutosaveChanges, LocalEditField>;

/**
 * The fields the editor is allowed to send back.
 *
 * Every key it produces is declared in `CHANGE_FIELD_SOURCES` above, so a
 * field added here without a home in the graft does not compile.
 */
function groupsForSave(quote: Quote): QuoteGroup[] {
  if (quote.groups && quote.groups.length > 0) return quote.groups;
  return [
    {
      id: '',
      name: '',
      nameTranslations: null,
      quantity: 1,
      notes: null,
      isDefault: true,
      displayOrder: 0,
    },
  ];
}

function groupIndexFor(quote: Quote, room: QuoteRoom): number {
  const groups = quote.groups ?? [];
  const index = groups.findIndex((group) => group.id === room.groupId);
  if (index >= 0) return index;
  const fallback = groups.findIndex((group) => group.isDefault);
  return fallback >= 0 ? fallback : 0;
}

export function buildChanges(quote: Quote): QuoteAutosaveChanges {
  return {
    dustAssessment: quote.dust?.assessment,
    dustContextSince: quote.dust?.contextSince,
    walkthroughAssessment: quote.walkthrough?.assessment,
    ...(quote.areaPricing
      ? {
          areaPricing: {
            mode: quote.areaPricing.mode,
            squareFeet: quote.areaPricing.squareFeet,
            rateCents: quote.areaPricing.rateCents,
            confirmed: quote.areaPricing.confirmed,
          },
        }
      : {}),
    // Always sent, null included, so the Worker never reads an older client's
    // area pricing first clean in its place.
    firstCleanPrice: quoteFirstCleanPrice(quote),
    customerId: quote.customerId,
    propertyId: quote.propertyId,
    quoteDate: quote.quoteDate,
    expirationDate: quote.expirationDate,
    hourlyRateCents: quote.totals.hourlyRateCents,
    billingIncrementMinutes: quote.totals.billingIncrementMinutes,
    crewSize: quote.crewSize,
    discountType: quote.totals.discountType,
    discountValue: quote.totals.discountValue,
    taxRateBasisPoints: quote.totals.taxRateBasisPoints,
    laborIsTaxable: quote.totals.laborIsTaxable,
    internalNotes: quote.internalNotes,
    customerNotes: quote.customerNotes,
    terms: quote.termsSnapshot,
    showTimeOnQuote: quote.showTimeOnQuote,
    showHourlyRateOnQuote: quote.showHourlyRateOnQuote,
    // The commercial shape (basis, visit count, agreement terms) rides every
    // autosave like the rest — segment itself is fixed at creation and never
    // sent back.
    billingBasis: quote.billingBasis,
    visitsPerWeek: quote.visitsPerWeek,
    contractTermMonths: quote.contract.termMonths,
    contractAutoRenews: quote.contract.autoRenews,
    contractEscalationBasisPoints: quote.contract.escalationBasisPoints,
    contractCancellationNoticeDays: quote.contract.cancellationNoticeDays,
    totalOverrideCents: quote.totals.totalOverrideCents,
    totalOverrideReason: quote.totals.totalOverrideReason,
    // The frequency offer rides every autosave like the discount does. The
    // derived cents on each tier stay behind — the Worker reprices them.
    // `??` because a recovered IndexedDB draft can predate migration 0110 and
    // carry neither field; replaying it must mean "no tiers", not a crash
    // that silently stops the autosave.
    frequencyTiersEnabled: quote.frequencyTiersEnabled ?? false,
    preferredFrequency: quote.preferredFrequency ?? null,
    frequencyTiers: (quote.frequencyTiers ?? []).map((tier) => ({
      frequency: tier.frequency,
      discountType: tier.discountType,
      discountValue: tier.discountValue,
    })),
    groups: groupsForSave(quote).map((group, index) => ({
      ...(isPersistedId(group.id) ? { id: group.id } : {}),
      name: group.name,
      nameTranslations: group.nameTranslations,
      quantity: group.quantity,
      notes: group.notes,
      isDefault: group.isDefault,
      displayOrder: index,
    })),
    rooms: quote.rooms.map((room, index) => ({
      ...(isPersistedId(room.id) ? { id: room.id } : {}),
      ...(isPersistedId(room.groupId) ? { groupId: room.groupId } : { groupIndex: groupIndexFor(quote, room) }),
      roomTemplateId: room.roomTemplateId,
      roomName: room.roomName,
      // Sent back with the name so the autosave does not strip the room's
      // other languages off the quote (see localized-text.ts).
      roomNameTranslations: room.roomNameTranslations,
      quantity: room.quantity,
      sizeCode: room.sizeCode,
      conditionCode: room.conditionCode,
      adjustedMinutes: room.adjustedMinutes,
      flatPriceCents: room.flatPriceCents,
      measuredSquareFeet: room.measuredSquareFeet,
      floorType: room.floorType,
      customerNotes: room.customerNotes,
      internalNotes: room.internalNotes,
      displayOrder: index,
      services: room.services.map((service, serviceIndex) => ({
        ...(isPersistedId(service.id) ? { id: service.id } : {}),
        serviceTemplateId: service.serviceTemplateId,
        name: service.name,
        description: service.description,
        nameTranslations: service.nameTranslations,
        descriptionTranslations: service.descriptionTranslations,
        estimatedMinutes: service.estimatedMinutes,
        isCustom: service.isCustom,
        isSelected: service.isSelected,
        showOnCustomerQuote: service.showOnCustomerQuote,
        isTaxable: service.isTaxable,
        frequency: service.frequency,
        measurementBasis: service.measurementBasis,
        measuredQuantity: service.measuredQuantity,
        productionRatePerHour: service.productionRatePerHour,
        firstVisitOnly: service.firstVisitOnly ?? false,
        customerNotes: service.customerNotes,
        internalNotes: service.internalNotes,
        displayOrder: serviceIndex,
      })),
    })),
    fees: quote.fees.map((fee, index) => ({
      ...(isPersistedId(fee.id) ? { id: fee.id } : {}),
      name: fee.name,
      description: fee.description,
      amountCents: fee.amountCents,
      isTaxable: fee.isTaxable,
      displayOrder: index,
    })),
  } as QuoteAutosaveChanges;
}

/** One field of the working copy, kept over the server's value. */
function keepLocalField<K extends LocalEditField>(target: Quote, local: Quote, field: K): void {
  target[field] = local[field];
}

/**
 * Rebuilds the editor's copy from a server response while keeping edits the
 * user made after that response was asked for.
 *
 * The server copy is the base, so every field the server owns — status,
 * sharing, sent and viewed stamps, the PDF pointer — is current. The fields
 * the editor writes are taken from the local copy, one list of them
 * (`LOCAL_EDIT_FIELDS`), tied to what `buildChanges` sends by
 * `CHANGE_FIELD_SOURCES`.
 */
export function graftLocalEdits(local: Quote, server: Quote, submitted?: Quote): Quote {
  const next = { ...server };
  for (const field of LOCAL_EDIT_FIELDS) keepLocalField(next, local, field);
  // A server-invalidated measurement is a fresh decision, even when the
  // in-flight edit was only a note. Bounds always belong to the server.
  // A second local context change cannot carry a confirmed price either.
  if (server.areaPricing) {
    const sameContext =
      local.customerId === server.customerId &&
      local.propertyId === server.propertyId &&
      local.currencyCode === server.currencyCode &&
      local.billingBasis === server.billingBasis;
    const unchangedPendingDecision =
      submitted?.areaPricing &&
      submitted.customerId === server.customerId &&
      submitted.propertyId === server.propertyId &&
      submitted.currencyCode === server.currencyCode &&
      submitted.billingBasis === server.billingBasis &&
      (['mode', 'squareFeet', 'rateCents', 'minRateCents', 'maxRateCents', 'confirmed'] as const).every(
        (key) => submitted.areaPricing?.[key] === server.areaPricing?.[key],
      );
    next.areaPricing =
      (!server.areaPricing.confirmed && !unchangedPendingDecision) || !sameContext || !local.areaPricing
        ? { ...server.areaPricing, ...(!sameContext ? { confirmed: false, squareFeet: null } : {}) }
        : {
            ...local.areaPricing,
            minRateCents: server.areaPricing.minRateCents,
            maxRateCents: server.areaPricing.maxRateCents,
          };
  }
  if (server.dust) {
    const sameContext =
      local.customerId === server.customerId &&
      local.propertyId === server.propertyId &&
      local.dust?.contextSince === server.dust.contextSince;
    const assessment = sameContext && local.dust ? local.dust.assessment : server.dust.assessment;
    next.dust = {
      ...server.dust,
      assessment,
      evidenceVerified:
        assessment?.photoId === server.dust.assessment?.photoId && sameContext ? server.dust.evidenceVerified : false,
    };
  }
  if (server.walkthrough) {
    const sameContext =
      local.customerId === server.customerId &&
      local.propertyId === server.propertyId &&
      local.currencyCode === server.currencyCode;
    next.walkthrough = {
      ...server.walkthrough,
      assessment: sameContext && local.walkthrough ? local.walkthrough.assessment : server.walkthrough.assessment,
    };
  }
  // A draft saved before groups existed has no list to keep. Taking it would
  // wipe the groups the server just sent back.
  if (local.groups === undefined) next.groups = server.groups;

  // Rooms are the editor's like the rest, but each one takes back the minutes
  // the Worker derived for it: those are priced server-side, and the room the
  // user is editing is the same row.
  const serverRoomsById = new Map(server.rooms.map((room) => [room.id, room]));
  next.rooms = local.rooms.map((room): QuoteRoom => {
    const serverRoom = serverRoomsById.get(room.id);
    return {
      ...room,
      ...(serverRoom ? { baseMinutes: serverRoom.baseMinutes, calculatedMinutes: serverRoom.calculatedMinutes } : {}),
    };
  });

  // A tier row is a pair: the estimator's discount inputs, which the editor
  // owns, and the two cents figures the Worker derives from them, which it
  // does not (`buildChanges` deliberately leaves those behind). Keeping a
  // whole local tier would pair a discount typed a moment ago with the price
  // of the discount before it — a customer-facing number that is wrong, and
  // stays wrong until a save succeeds. So the inputs stay local and the cents
  // stay the server's; `repricingFrequencies` below says which pairs do not
  // yet belong together, so a surface showing a tier price can say it is being
  // worked out instead of showing one that is not.
  const serverTiersByFrequency = new Map((server.frequencyTiers ?? []).map((tier) => [tier.frequency, tier]));
  next.frequencyTiers = (local.frequencyTiers ?? []).map((tier): QuoteFrequencyTier => {
    const priced = serverTiersByFrequency.get(tier.frequency);
    return {
      ...tier,
      frequencyDiscountCents: priced?.frequencyDiscountCents ?? 0,
      totalCents: priced?.totalCents ?? 0,
    };
  });

  return next;
}

/**
 * The offered frequencies whose stored price does not describe the discount
 * the editor is showing.
 *
 * The Worker prices the tiers; between an edit and the response that answers
 * it, the quote carries the discount the estimator just typed beside the price
 * of the one before it. That is the whole window this reports on — and it
 * stays open if the save fails, which is exactly when a stale price must not
 * be read as the customer's.
 *
 * An empty answer for a quote with no server copy yet (a cold offline open):
 * nothing to compare against is not evidence of a mismatch.
 */
export function repricingFrequencies(local: Quote, server: Quote | null | undefined): SeriesFrequency[] {
  if (!server) return [];
  const priced = new Map((server.frequencyTiers ?? []).map((tier) => [tier.frequency, tier]));
  return (local.frequencyTiers ?? [])
    .filter((tier) => {
      const match = priced.get(tier.frequency);
      return !match || match.discountType !== tier.discountType || match.discountValue !== tier.discountValue;
    })
    .map((tier) => tier.frequency);
}

/**
 * Applies server-assigned identifiers and computed figures to a local copy the
 * user has kept editing during the save.
 *
 * Rows are located by id through the map above, so reordering, inserting and
 * removing while a save is in flight are all safe.
 */
export function mergeServerIds(local: Quote, submitted: Quote, server: Quote): Quote {
  return graftLocalEdits(resolveIds(local, buildIdMap(submitted, server)), server, submitted);
}
